/**
 * The score keys a scan precomputes for every report, one per WCAG conformance
 * target. They exist independently of the target a report has selected, so any
 * report can be read at any target without recalculation.
 */
export const BENCHMARK_TARGET_KEYS = [
  'TargetA',
  'TargetA_BP',
  'TargetAA',
  'TargetAA_BP',
  'TargetAAA',
  'TargetAAA_BP',
] as const;

export type BenchmarkTargetKey = (typeof BENCHMARK_TARGET_KEYS)[number];

const KEY_BY_NAME = new Map<string, BenchmarkTargetKey>(
  BENCHMARK_TARGET_KEYS.map((key) => [key.toLowerCase(), key]),
);

/**
 * Score key for a conformance target as stored in `auditEntries`
 * (`'AA + Best Practice'` -> `'TargetAA_BP'`), or null for anything else. The
 * split mirrors `calcOverallAuditScore` in the dashboard, which builds the same
 * key to read a report's own score.
 */
export function toBenchmarkTargetKey(
  target: unknown,
): BenchmarkTargetKey | null {
  if (typeof target !== 'string') return null;

  const trimmed = target.trim();
  const direct = KEY_BY_NAME.get(trimmed.toLowerCase());
  if (direct) return direct;

  const [level, suffix] = trimmed.split(' + ');
  if (!level) return null;

  return (
    KEY_BY_NAME.get(`target${level}${suffix ? '_bp' : ''}`.toLowerCase()) ??
    null
  );
}
